import { useEffect, useState } from 'react';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import { MasterSubCategory } from 'domain/entities/MasterEntity';
import { DetailProperty } from 'domain/entities/PropertyEntity';
import { ImageComponent } from 'presentation/components/ImageComponent';
import { openErrorNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { useParams } from 'react-router-dom';
import { COMMON, MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import FallbackImg from 'assets/images/FallbackImg.svg';
import { Detail } from './components/Detail';

interface props {
    propertyUseCase: PropertyUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
}

interface MasterOptions {
    structure: MasterSubCategory[];
}

const CU04902Page = ({ propertyUseCase, masterUseCase }: props) => {
    const dispatch = useAppDispatch();
    const { ListedPropertyId } = useParams();
    const [detailProperty, setDetailProperty] = useState<DetailProperty>();
    const [masterData, setMasterData] = useState<MasterOptions>();
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        getListedPropertyDetail({ propertyId: Number(ListedPropertyId) });
    }, []);

    const getListedPropertyDetail = async (params: { propertyId: number }) => {
        try {
            dispatch(startFetching());
            const responses = await Promise.all([initiateOptions(), propertyUseCase.getDetailProperty(params)]);
            const masterData = responses[0];
            const detailProperty = responses[1]?.result;
            setMasterData(masterData);
            if (detailProperty) {
                const structure = detailProperty.structure;
                const structureName = masterData.structure.find(
                    (item) => item.code == structure,
                )?.codeName;
                detailProperty.structureName = structureName;
                setDetailProperty(detailProperty);
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const initiateOptions = async (): Promise<MasterOptions> => {
        try {
            dispatch(startFetching());

            const options = await Promise.all([
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.STRUCTURE_CODE,
                }),
            ]);
            return {
                structure: options[0].result,
            };
        } catch (error: any) {
            return {
                structure: [],
            };
        } finally {
            dispatch(completeFetching());
        }
    };

    const nextImage = () => {
        if (detailProperty && detailProperty.propertyImages) {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % detailProperty.propertyImages.length);
        }
    };

    const prevImage = () => {
        if (detailProperty && detailProperty.propertyImages) {
            setCurrentIndex((prevIndex) => (prevIndex - 1 + detailProperty.propertyImages.length) % detailProperty.propertyImages.length);
        }
    };

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexFlow: 'column' }} className="CU-049-02-container">
            <PageTitle title="物件詳細" />
            <section className="width-full-screen height-full-screen center-inside ">
                <div>
                    <div className="text-title">{detailProperty?.propertyName ?? COMMON.EMPTY}</div>
                    <section style={{ padding: '0px 32px 37px 0px' }}>
                        <div style={{ display: 'flex', marginBottom: 20 }}>
                            <div style={{ position: 'relative', width: '380px', height: '480px', overflow: 'hidden', marginRight: 20 }}>
                                <button
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '10px',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={prevImage}
                                >
                                    {'<'}
                                </button>

                                <div style={{ display: 'flex', transition: 'transform 0.5s ease-in-out', transform: `translateX(-${currentIndex * 100}%)`, width: '100%' }}>
                                    {(detailProperty?.propertyImages && detailProperty.propertyImages.length > 0) ? (
                                        detailProperty.propertyImages.map((image, index) => (
                                            <div key={index} style={{ minWidth: '100%', boxSizing: 'border-box' }}>
                                                <ImageComponent
                                                    width="380px"
                                                    height="480px"
                                                    alt="Property Image"
                                                    src={image.fileUrl}  // Correctly set the image source here
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        // Display Fallback image if no images are available
                                        <div style={{ minWidth: '100%', boxSizing: 'border-box' }}>
                                            <ImageComponent
                                                width="380px"
                                                height="480px"
                                                alt="Fallback Image"
                                                src={FallbackImg}  // Show fallback image
                                            />
                                        </div>
                                    )}
                                </div>

                                <button
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={nextImage}
                                >
                                    {'>'}
                                </button>
                            </div>

                            <Detail data={detailProperty} />
                        </div>
                    </section>
                </div>
            </section>
        </div>
    );
};

export default CU04902Page;
